/**
 * Open-Meteo : qualité de l'air modélisée (CAMS Europe) et vent à trois
 * altitudes. Aucune clé d'API.
 */
import type { AirSnapshot, WeatherSnapshot, WindLevelKey, WindSample } from '../core/types';
import { epochFromNaiveLocal, finiteOrNull, getJson } from './http';

/**
 * Fuseau demandé explicitement, plutôt que `timezone=auto`.
 *
 * L'application ne couvre que la France métropolitaine, et un fuseau fixe
 * rend les journées du bloc `daily` conformes aux journées françaises, quel
 * que soit l'endroit d'où le site est consulté.
 */
const TIMEZONE = 'Europe/Paris';

/** Les séries horaires sont tronquées un peu après maintenant : le futur ne
 *  sert pas à savoir depuis quand le vent souffle dans sa direction. */
const FUTURE_TOLERANCE_MS = 30 * 60 * 1000;

const AIR_ENDPOINT = 'https://air-quality-api.open-meteo.com/v1/air-quality';
const FORECAST_ENDPOINT = 'https://api.open-meteo.com/v1/forecast';

interface AirResponse {
  utc_offset_seconds?: number;
  current?: {
    time?: string;
    pm2_5?: number;
    pm10?: number;
    carbon_monoxide?: number;
    nitrogen_dioxide?: number;
    ozone?: number;
    sulphur_dioxide?: number;
  };
  hourly?: {
    time?: string[];
    pm2_5?: (number | null)[];
  };
}

export async function fetchAir(
  lat: number,
  lon: number,
  now: number = Date.now(),
): Promise<AirSnapshot> {
  const url =
    `${AIR_ENDPOINT}?latitude=${lat}&longitude=${lon}` +
    '&current=pm2_5,pm10,carbon_monoxide,nitrogen_dioxide,ozone,sulphur_dioxide' +
    `&hourly=pm2_5&past_days=1&forecast_days=1&timezone=${encodeURIComponent(TIMEZONE)}`;

  const data = await getJson<AirResponse>(url);
  const offset = finiteOrNull(data.utc_offset_seconds) ?? 0;
  const current = data.current;

  const snapshot: AirSnapshot = {
    pm25: null,
    at: null,
    nextPm25: null,
    pm10: finiteOrNull(current?.pm10),
    co: finiteOrNull(current?.carbon_monoxide),
    no2: finiteOrNull(current?.nitrogen_dioxide),
    o3: finiteOrNull(current?.ozone),
    so2: finiteOrNull(current?.sulphur_dioxide),
  };

  const currentPm25 = finiteOrNull(current?.pm2_5);
  const currentAt = epochFromNaiveLocal(current?.time, offset);
  if (currentPm25 !== null && currentAt !== null) {
    snapshot.pm25 = currentPm25;
    snapshot.at = currentAt;
  }

  // Série horaire : sert à anticiper l'heure suivante, et de repli si la
  // valeur courante manque.
  const times = data.hourly?.time ?? [];
  const values = data.hourly?.pm2_5 ?? [];
  const points: { at: number; value: number }[] = [];
  for (let i = 0; i < times.length; i += 1) {
    const at = epochFromNaiveLocal(times[i], offset);
    const value = finiteOrNull(values[i]);
    if (at !== null && value !== null) points.push({ at, value });
  }

  snapshot.nextPm25 = points.find((point) => point.at > now)?.value ?? null;

  if (snapshot.pm25 === null) {
    const past = points.filter((point) => point.at <= now);
    const last = past[past.length - 1];
    if (last) {
      snapshot.pm25 = last.value;
      snapshot.at = last.at;
    }
  }

  return snapshot;
}

interface ForecastResponse {
  utc_offset_seconds?: number;
  current?: {
    time?: string;
    temperature_2m?: number;
    wind_speed_10m?: number;
    wind_direction_10m?: number;
  };
  hourly?: Record<string, unknown>;
  daily?: { temperature_2m_max?: (number | null)[] };
}

/** Noms des champs Open-Meteo pour chaque altitude suivie. */
const LEVEL_FIELDS: Readonly<Record<WindLevelKey, { direction: string; speed: string }>> = {
  '180m': { direction: 'wind_direction_180m', speed: 'wind_speed_180m' },
  '850hPa': { direction: 'wind_direction_850hPa', speed: 'wind_speed_850hPa' },
  '700hPa': { direction: 'wind_direction_700hPa', speed: 'wind_speed_700hPa' },
};

export async function fetchWeather(
  lat: number,
  lon: number,
  now: number = Date.now(),
): Promise<WeatherSnapshot | null> {
  const hourlyFields = Object.values(LEVEL_FIELDS)
    .flatMap((field) => [field.direction, field.speed])
    .join(',');

  const url =
    `${FORECAST_ENDPOINT}?latitude=${lat}&longitude=${lon}` +
    '&current=temperature_2m,wind_speed_10m,wind_direction_10m' +
    `&hourly=${hourlyFields}` +
    '&daily=temperature_2m_max&past_days=2&forecast_days=1' +
    `&timezone=${encodeURIComponent(TIMEZONE)}`;

  const data = await getJson<ForecastResponse>(url);
  const offset = finiteOrNull(data.utc_offset_seconds) ?? 0;
  const currentAt = epochFromNaiveLocal(data.current?.time, offset);
  if (currentAt === null) return null;

  const times = (data.hourly?.['time'] as string[] | undefined) ?? [];
  const series = {} as Record<WindLevelKey, WindSample[]>;

  for (const key of Object.keys(LEVEL_FIELDS) as WindLevelKey[]) {
    const fields = LEVEL_FIELDS[key];
    const directions = data.hourly?.[fields.direction] as (number | null)[] | undefined;
    const speeds = data.hourly?.[fields.speed] as (number | null)[] | undefined;

    const samples: WindSample[] = [];
    for (let i = 0; i < times.length; i += 1) {
      const at = epochFromNaiveLocal(times[i], offset);
      const direction = finiteOrNull(directions?.[i]);
      // Sans direction, le point n'apprend rien sur le régime de vent.
      if (at === null || direction === null) continue;
      if (at > now + FUTURE_TOLERANCE_MS) continue;
      samples.push({ at, direction, speed: finiteOrNull(speeds?.[i]) });
    }
    series[key] = samples;
  }

  const dailyMax = data.daily?.temperature_2m_max;

  return {
    direction: finiteOrNull(data.current?.wind_direction_10m),
    speed: finiteOrNull(data.current?.wind_speed_10m),
    temperature: finiteOrNull(data.current?.temperature_2m),
    temperatureMax: Array.isArray(dailyMax) ? finiteOrNull(dailyMax[0]) : null,
    at: currentAt,
    series,
  };
}
